import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Building2,
  Eye,
  Image,
  Loader2,
  Menu,
  Plus,
  Save,
  Star,
  Trash2,
  Upload,
  User,
  Users,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { SheetTrigger } from '@/components/ui/sheet'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { authenticatedOutputUrl, useOutputAuthToken } from '@/features/output/output-url'
import {
  useBrandAssets,
  useBrandProfiles,
  useCreateBrandProfile,
  useDeleteManagedBrandAsset,
  useDeleteBrandProfile,
  useUpdateBrandStyleguide,
  useUpdateManagedBrandAsset,
  useUpdateBrandProfile,
  useUploadManagedBrandAsset,
} from '@/features/settings/use-brand'
import type {
  BrandAsset,
  BrandAssetType,
  BrandProfile,
  BrandStyleguide,
  BrandType,
} from '@/features/settings/brand-api'
import { VoiceSourcesCard } from '@/features/brand/voice-sources/voice-sources-card'

export function SettingsBrandPage() {
  const profilesQuery = useBrandProfiles()
  const [activeId, setActiveId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const profiles = profilesQuery.data ?? []
  const active = profiles.find((p) => p.id === activeId) ?? profiles[0] ?? null

  useEffect(() => {
    if (!activeId && profiles[0]) setActiveId(profiles[0].id)
  }, [profiles, activeId])

  return (
    <div className="flex h-full">
      {/* Desktop sidebar */}
      <aside className="hidden w-72 border-r border-border bg-muted/20 lg:flex lg:flex-col">
        <BrandProfileNav
          profiles={profiles}
          isLoading={profilesQuery.isLoading}
          activeId={active?.id ?? null}
          onSelect={setActiveId}
          onCreate={() => setCreateOpen(true)}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile Nav-Bar (M8.4) — spiegelt das App-Shell-Pattern */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 lg:hidden">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="-ml-1 inline-flex h-11 w-11 min-[761px]:h-9 min-[761px]:w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Brand-Navigation öffnen"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Brand-Navigation</SheetTitle>
              </SheetHeader>
              <BrandProfileNav
                profiles={profiles}
                isLoading={profilesQuery.isLoading}
                activeId={active?.id ?? null}
                onSelect={setActiveId}
                onCreate={() => setCreateOpen(true)}
                onNavigate={() => setMobileNavOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <span className="text-sm font-semibold">Brand-Profile</span>
        </div>

        <main className="flex-1 overflow-y-auto px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
          {active ? <BrandEditor key={active.id} profile={active} /> : <EmptyState />}
        </main>
      </div>

      <CreateBrandDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(id) => setActiveId(id)} />
    </div>
  )
}

interface BrandProfileNavProps {
  profiles: BrandProfile[]
  isLoading: boolean
  activeId: string | null
  onSelect: (id: string) => void
  onCreate: () => void
  onNavigate?: () => void
}

function BrandProfileNav({ profiles, isLoading, activeId, onSelect, onCreate, onNavigate }: BrandProfileNavProps) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Brand-Profile</h2>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onCreate()
            onNavigate?.()
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <ul className="space-y-1 p-2">
        {isLoading ? (
          <li className="px-2 py-3 text-xs text-muted-foreground">Lädt…</li>
        ) : profiles.length === 0 ? (
          <li className="px-2 py-3 text-xs text-muted-foreground">Keine Profile.</li>
        ) : (
          profiles.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  onSelect(p.id)
                  onNavigate?.()
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded border-l-2 border-transparent px-2 py-1.5 text-left text-sm hover:bg-muted',
                  activeId === p.id && 'bg-muted font-medium',
                  p.is_default && 'border-l-yellow-400 bg-yellow-50/60 dark:bg-yellow-950/20',
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full border"
                    style={{ backgroundColor: p.color_primary ?? 'transparent' }}
                  />
                  <span className="min-w-0">
                    <span className="block break-words">{p.name}</span>
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                      {p.brand_type}
                    </span>
                  </span>
                </span>
                {p.is_default && (
                  <Badge variant="outline" className="ml-2 shrink-0 gap-1 px-1.5 py-0 text-[10px]">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    Default
                  </Badge>
                )}
              </button>
            </li>
          ))
        )}
      </ul>
    </>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="max-w-sm space-y-2 text-center">
        <p className="text-sm text-muted-foreground">Kein Brand-Profil ausgewählt.</p>
      </div>
    </div>
  )
}

interface BrandEditorProps {
  profile: BrandProfile
}

function BrandEditor({ profile }: BrandEditorProps) {
  const updateMutation = useUpdateBrandProfile()
  const deleteMutation = useDeleteBrandProfile()

  const [form, setForm] = useState({
    name: profile.name,
    brand_type: profile.brand_type,
    domain_url: profile.domain_url ?? '',
    speaker_role: profile.speaker_role ?? '',
    default_model: profile.default_model ?? 'anthropic/claude-sonnet-4-6',
    color_primary: profile.color_primary ?? '#1E3A5F',
    color_accent: profile.color_accent ?? '#5B8DEF',
    color_neutral: profile.color_neutral ?? '#71717A',
    tone: profile.tone ?? '',
    user_bio: profile.user_bio ?? '',
    company_description: profile.company_description ?? '',
    do_nots: profile.do_nots.join('\n'),
    typography_heading:
      typeof profile.typography['font_heading'] === 'string'
        ? (profile.typography['font_heading'] as string)
        : 'Inter',
    typography_body:
      typeof profile.typography['font_body'] === 'string'
        ? (profile.typography['font_body'] as string)
        : 'Inter',
    linkedin_profile_url: profile.linkedin_profile_url ?? '',
    linkedin_bio: profile.linkedin_bio ?? '',
    x_handle: profile.x_handle ?? '',
    language_variant: profile.language_variant ?? 'de-CH',
  })

  // Sync form bei Profile-Wechsel
  useEffect(() => {
    setForm({
      name: profile.name,
      brand_type: profile.brand_type,
      domain_url: profile.domain_url ?? '',
      speaker_role: profile.speaker_role ?? '',
      default_model: profile.default_model ?? 'anthropic/claude-sonnet-4-6',
      color_primary: profile.color_primary ?? '#1E3A5F',
      color_accent: profile.color_accent ?? '#5B8DEF',
      color_neutral: profile.color_neutral ?? '#71717A',
      tone: profile.tone ?? '',
      user_bio: profile.user_bio ?? '',
      company_description: profile.company_description ?? '',
      do_nots: profile.do_nots.join('\n'),
      typography_heading:
        typeof profile.typography['font_heading'] === 'string'
          ? (profile.typography['font_heading'] as string)
          : 'Inter',
      typography_body:
        typeof profile.typography['font_body'] === 'string'
          ? (profile.typography['font_body'] as string)
          : 'Inter',
      linkedin_profile_url: profile.linkedin_profile_url ?? '',
      linkedin_bio: profile.linkedin_bio ?? '',
      x_handle: profile.x_handle ?? '',
      language_variant: profile.language_variant ?? 'de-CH',
    })
  }, [profile.id, profile.name, profile.brand_type, profile.domain_url, profile.speaker_role, profile.default_model, profile.color_primary, profile.color_accent, profile.color_neutral, profile.tone, profile.user_bio, profile.company_description, profile.do_nots, profile.typography, profile.linkedin_profile_url, profile.linkedin_bio, profile.x_handle, profile.language_variant])

  const setDefault = async () => {
    try {
      await updateMutation.mutateAsync({ id: profile.id, input: { is_default: true } })
      toast.success(`${profile.name} ist jetzt Default`)
    } catch {
      toast.error('Default-Wechsel fehlgeschlagen')
    }
  }

  const handleDelete = async () => {
    if (!confirm(`Profil "${profile.name}" wirklich löschen?`)) return
    try {
      await deleteMutation.mutateAsync(profile.id)
      toast.success('Gelöscht')
    } catch {
      toast.error('Löschen fehlgeschlagen')
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="break-words text-[26px] font-[650] leading-tight">{profile.name}</h1>
            {profile.is_default && (
              <Badge variant="outline">
                <Star className="mr-1 h-3 w-3 fill-yellow-400 text-yellow-400" />
                Default
              </Badge>
            )}
          </div>
          <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{profile.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          {!profile.is_default && (
            <Button variant="outline" size="sm" onClick={() => void setDefault()}>
              <Star className="mr-1.5 h-3.5 w-3.5" />
              Als Default
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => void handleDelete()}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <BrandStyleguidePanel
        profile={profile}
        fallbackForm={{
          color_primary: form.color_primary,
          color_accent: form.color_accent,
          color_neutral: form.color_neutral,
          typography_heading: form.typography_heading,
          typography_body: form.typography_body,
        }}
        onFallbackChange={(patch) => setForm({ ...form, ...patch })}
      />

      <BrandAssetsSection profile={profile} />

      <VoiceSourcesCard profile={profile} />
    </div>
  )
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 min-[761px]:grid-cols-3 min-[761px]:gap-3 items-start">
      <Label className="min-[761px]:pt-2 text-sm text-muted-foreground">{label}</Label>
      <div className="min-[761px]:col-span-2">{children}</div>
    </div>
  )
}

function BrandTypeControl({
  value,
  onChange,
}: {
  value: BrandType
  onChange: (value: BrandType) => void
}) {
  const items: Array<{ value: BrandType; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { value: 'person', label: 'Person', icon: User },
    { value: 'company', label: 'Company', icon: Building2 },
    { value: 'persona', label: 'Persona', icon: Users },
  ]
  return (
    <FormRow label="Typ">
      <div className="grid grid-cols-1 gap-2 min-[761px]:grid-cols-3">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => onChange(item.value)}
              className={cn(
                'flex min-h-11 items-center justify-center gap-2 rounded-md border border-border px-3 text-[16px] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:min-h-9 min-[761px]:text-sm',
                value === item.value && 'border-primary bg-primary/10 text-primary',
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </button>
          )
        })}
      </div>
    </FormRow>
  )
}

const BRAND_ASSET_TYPE_LABELS: Record<BrandAssetType, string> = {
  logo_primary: 'Logo Primary',
  logo_inverse: 'Logo Inverse',
  logo_icon: 'Logo Icon',
  gradient: 'Gradient',
  texture: 'Texture',
  background_pattern: 'Pattern',
  icon_set: 'Icon Set',
  custom_image: 'Custom Image',
}

const SLOT_ROLE_OPTIONS = [
  'cover_hero',
  'footer_logo',
  'section_divider',
  'cta_bg',
  'chart_mark',
  'quote_mark',
]

interface PendingBrandAsset {
  id: string
  file: File
  assetType: BrandAssetType
  slotRole: string
  status: 'idle' | 'uploading' | 'error'
  errorMessage?: string
}

function detectBrandAssetType(filename: string): BrandAssetType {
  const lower = filename.toLowerCase()
  if (/inverse|white|weiss|light/.test(lower) && /logo/.test(lower)) return 'logo_inverse'
  if (/icon|mark|favicon|symbol/.test(lower)) return 'logo_icon'
  if (/logo/.test(lower)) return 'logo_primary'
  if (/gradient|verlauf/.test(lower)) return 'gradient'
  if (/texture|textur/.test(lower)) return 'texture'
  if (/pattern|muster/.test(lower)) return 'background_pattern'
  if (/icons?|set/.test(lower)) return 'icon_set'
  return 'custom_image'
}

function defaultSlotRole(assetType: BrandAssetType): string {
  if (assetType === 'logo_primary' || assetType === 'logo_inverse' || assetType === 'logo_icon') {
    return 'footer_logo'
  }
  if (assetType === 'gradient') return 'cover_hero'
  if (assetType === 'background_pattern' || assetType === 'texture') return 'section_divider'
  return ''
}

function BrandAssetsSection({ profile }: { profile: BrandProfile }) {
  const token = useOutputAuthToken()
  const assetsQuery = useBrandAssets(profile.id)
  const upload = useUploadManagedBrandAsset(profile.id)
  const update = useUpdateManagedBrandAsset(profile.id)
  const deleteAsset = useDeleteManagedBrandAsset(profile.id)
  const [pending, setPending] = useState<PendingBrandAsset[]>([])
  const [preview, setPreview] = useState<BrandAsset | null>(null)

  const assets = (assetsQuery.data ?? []).slice().sort((a, b) => {
    if (a.display_order !== b.display_order) return a.display_order - b.display_order
    return a.created_at?.localeCompare(b.created_at ?? '') ?? 0
  })
  const anyUploading = pending.some((item) => item.status === 'uploading') || upload.isPending

  const handleFilesPicked = (files: File[]) => {
    setPending((prev) => [
      ...prev,
      ...files.map((file) => {
        const assetType = detectBrandAssetType(file.name)
        return {
          id: newPendingId(),
          file,
          assetType,
          slotRole: defaultSlotRole(assetType),
          status: 'idle' as const,
        }
      }),
    ])
  }

  const dropzone = useDropzone({
    multiple: true,
    disabled: anyUploading,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/svg+xml': ['.svg'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
    },
    onDrop: handleFilesPicked,
  })

  const updatePending = (id: string, patch: Partial<PendingBrandAsset>) => {
    setPending((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }

  const uploadAll = async () => {
    const snapshot = pending
    if (snapshot.length === 0) return
    setPending((prev) => prev.map((item) => ({ ...item, status: 'uploading', errorMessage: undefined })))
    const results = await Promise.allSettled(
      snapshot.map((item, index) =>
        upload
          .mutateAsync({
            file: item.file,
            assetType: item.assetType,
            slotRole: item.slotRole || undefined,
            displayOrder: assets.length + index,
          })
          .then(() => item.id),
      ),
    )
    setPending((prev) =>
      prev.flatMap((item) => {
        const idx = snapshot.findIndex((candidate) => candidate.id === item.id)
        const result = idx >= 0 ? results[idx] : undefined
        if (!result) return [item]
        if (result.status === 'fulfilled') return []
        return [{
          ...item,
          status: 'error' as const,
          errorMessage: result.reason instanceof Error ? result.reason.message : 'Upload fehlgeschlagen',
        }]
      }),
    )
    const ok = results.filter((result) => result.status === 'fulfilled').length
    if (ok > 0) toast.success(`${ok} Brand-Asset(s) hochgeladen`)
  }

  const updateSlotRole = async (asset: BrandAsset, slotRole: string) => {
    try {
      await update.mutateAsync({
        assetId: asset.id,
        input: { slotRole: slotRole || null },
      })
      toast.success('Slot aktualisiert')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Slot-Update fehlgeschlagen')
    }
  }

  const removeAsset = async (asset: BrandAsset) => {
    if (!confirm(`Asset "${asset.metadata.original_name ?? asset.asset_type}" löschen?`)) return
    try {
      await deleteAsset.mutateAsync(asset.id)
      toast.success('Asset gelöscht')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Löschen fehlgeschlagen')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand-Assets</CardTitle>
        <p className="text-sm text-muted-foreground">
          Wird verwendet in: /presentation. Logos, Gradients und Slot-Rollen landen direkt im
          BrandContract und werden beim PDF-Render als echte Bilddateien eingebettet.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          {...dropzone.getRootProps({
            className: cn(
              'flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border px-3 py-6 text-sm text-muted-foreground hover:bg-muted',
              dropzone.isDragActive && 'border-primary bg-primary/5 text-primary',
              anyUploading && 'cursor-not-allowed opacity-60',
            ),
          })}
        >
          <input {...dropzone.getInputProps()} />
          <Upload className="h-4 w-4" />
          <span>{dropzone.isDragActive ? 'Assets hier ablegen' : 'Logos, Gradients, Texturen oder Patterns hochladen'}</span>
        </div>

        {pending.length > 0 && (
          <div className="space-y-2">
            {pending.map((item) => (
              <PendingBrandAssetUpload
                key={item.id}
                item={item}
                disabled={anyUploading}
                onChange={(patch) => updatePending(item.id, patch)}
                onRemove={() => setPending((prev) => prev.filter((candidate) => candidate.id !== item.id))}
              />
            ))}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={anyUploading} onClick={() => setPending([])}>
                Abbrechen
              </Button>
              <Button size="sm" disabled={anyUploading || pending.length === 0} onClick={() => void uploadAll()}>
                {anyUploading && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Alle hochladen ({pending.length})
              </Button>
            </div>
          </div>
        )}

        {assetsQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">Assets werden geladen...</p>
        ) : assets.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Noch keine Render-Assets. Logos und Gradients hier hochladen, damit Präsentationen echte Brand-Dateien verwenden.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
            {assets.map((asset) => (
              <BrandAssetCard
                key={asset.id}
                asset={asset}
                token={token}
                updating={update.isPending}
                deleting={deleteAsset.isPending}
                onPreview={() => setPreview(asset)}
                onSlotRoleChange={(slotRole) => void updateSlotRole(asset, slotRole)}
                onDelete={() => void removeAsset(asset)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{preview ? BRAND_ASSET_TYPE_LABELS[preview.asset_type] : 'Brand-Asset'}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="overflow-hidden rounded-md border border-border bg-muted/30">
              <BrandAssetImage
                asset={preview}
                token={token}
                className="max-h-[70vh] w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function PendingBrandAssetUpload({
  item,
  disabled,
  onChange,
  onRemove,
}: {
  item: PendingBrandAsset
  disabled: boolean
  onChange: (patch: Partial<PendingBrandAsset>) => void
  onRemove: () => void
}) {
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="break-all text-sm font-medium">{item.file.name}</p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {Math.round(item.file.size / 1024)} KB
            {item.status === 'uploading' && ' · wird hochgeladen'}
          </p>
        </div>
        <Button size="icon" variant="ghost" disabled={disabled} onClick={onRemove} title="Entfernen">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
        <div>
          <Label className="text-sm text-muted-foreground">Typ</Label>
          <select
            value={item.assetType}
            disabled={disabled}
            onChange={(e) => {
              const assetType = e.target.value as BrandAssetType
              onChange({ assetType, slotRole: item.slotRole || defaultSlotRole(assetType) })
            }}
            className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-9 min-[761px]:text-sm"
          >
            {Object.entries(BRAND_ASSET_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-sm text-muted-foreground">Slot</Label>
          <select
            value={item.slotRole}
            disabled={disabled}
            onChange={(e) => onChange({ slotRole: e.target.value })}
            className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-9 min-[761px]:text-sm"
          >
            <option value="">Kein Slot</option>
            {SLOT_ROLE_OPTIONS.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
        </div>
      </div>
      {item.status === 'error' && item.errorMessage && (
        <p className="mt-2 rounded border border-red-300 bg-white/60 px-2 py-1 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
          {item.errorMessage}
        </p>
      )}
    </div>
  )
}

function BrandAssetCard({
  asset,
  token,
  updating,
  deleting,
  onPreview,
  onSlotRoleChange,
  onDelete,
}: {
  asset: BrandAsset
  token: string | null
  updating: boolean
  deleting: boolean
  onPreview: () => void
  onSlotRoleChange: (slotRole: string) => void
  onDelete: () => void
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <button
        type="button"
        onClick={onPreview}
        className="group relative flex aspect-[16/9] w-full items-center justify-center bg-muted/40"
      >
        <BrandAssetImage
          asset={asset}
          token={token}
          className="h-full w-full object-contain p-3"
        />
        <span className="absolute right-2 top-2 rounded bg-background/90 p-1 opacity-0 shadow-sm transition group-hover:opacity-100">
          <Eye className="h-4 w-4" />
        </span>
      </button>
      <div className="space-y-2 border-t border-border p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Badge variant="outline">{BRAND_ASSET_TYPE_LABELS[asset.asset_type]}</Badge>
            <p className="mt-1 break-all font-mono text-[11px] text-muted-foreground">
              {String(asset.metadata.original_name ?? asset.storage_path.split('/').pop() ?? asset.id)}
            </p>
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
            disabled={deleting}
            onClick={onDelete}
            title="Löschen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Slot</Label>
          <select
            value={asset.slot_role ?? ''}
            disabled={updating}
            onChange={(e) => onSlotRoleChange(e.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-9 min-[761px]:text-sm"
          >
            <option value="">Kein Slot</option>
            {SLOT_ROLE_OPTIONS.map((slot) => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

function BrandAssetImage({
  asset,
  token,
  className,
}: {
  asset: BrandAsset
  token: string | null
  className?: string
}) {
  const { url, error } = useAuthenticatedBrandAssetObjectUrl(asset.proxy_url, token)
  const alt = String(asset.metadata.original_name ?? asset.asset_type)

  if (url) {
    return (
      <img
        key={url}
        src={url}
        alt={alt}
        className={className}
        loading="lazy"
      />
    )
  }

  if (error) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center text-xs text-destructive">
        <Image className="h-5 w-5" />
        <span>Preview konnte nicht geladen werden</span>
      </div>
    )
  }

  return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
}

function useAuthenticatedBrandAssetObjectUrl(source: string | undefined, token: string | null) {
  const [state, setState] = useState<{ url: string | null; error: string | null }>({ url: null, error: null })

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    if (!source || !token) {
      setState({ url: null, error: null })
      return undefined
    }

    const url = authenticatedOutputUrl(source, null)
    if (!url) {
      setState({ url: null, error: 'missing_url' })
      return undefined
    }

    setState({ url: null, error: null })
    void fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const blob = await response.blob()
        if (!blob.type.startsWith('image/')) throw new Error(`Unexpected content-type ${blob.type}`)
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setState({ url: objectUrl, error: null })
      })
      .catch((err) => {
        if (!cancelled) setState({ url: null, error: err instanceof Error ? err.message : String(err) })
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [source, token])

  return state
}

function newPendingId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function findStyleColor(colors: BrandStyleguide['colors'] | undefined, name: string): string | null {
  const match = colors?.find((color) => color.name.toLowerCase().includes(name.toLowerCase()))
  return match?.hex ?? null
}

function formatStyleColors(colors: BrandStyleguide['colors'] | undefined): string {
  return (colors ?? [])
    .map((color) => [color.name, color.hex, color.usage ?? ''].filter(Boolean).join(' | '))
    .join('\n')
}

function parseStyleColors(value: string): NonNullable<BrandStyleguide['colors']> {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name = 'Color', hex = '', usage] = line.split('|').map((part) => part.trim())
      return { name, hex, usage: usage || undefined }
    })
    .filter((color) => color.hex.length > 0)
}

function ensureNamedColors(
  colors: NonNullable<BrandStyleguide['colors']>,
  named: { primary: string; accent: string; neutral: string },
): NonNullable<BrandStyleguide['colors']> {
  const byName = (name: string) =>
    colors.some((color) => color.name.toLowerCase().includes(name.toLowerCase()))
  const next = [...colors]
  if (named.primary && !byName('primary')) next.unshift({ name: 'Primary', hex: named.primary })
  if (named.accent && !byName('accent')) next.push({ name: 'Accent', hex: named.accent })
  if (named.neutral && !byName('neutral')) next.push({ name: 'Neutral', hex: named.neutral })
  return next
}

function BrandStyleguidePanel({
  profile,
  fallbackForm,
  onFallbackChange,
}: {
  profile: BrandProfile
  fallbackForm: {
    color_primary: string
    color_accent: string
    color_neutral: string
    typography_heading: string
    typography_body: string
  }
  onFallbackChange: (patch: Partial<typeof fallbackForm>) => void
}) {
  const updateStyleguide = useUpdateBrandStyleguide(profile.id)
  const sg = profile.brand_styleguide ?? {}
  const primaryColor = findStyleColor(sg.colors, 'primary') ?? sg.colors?.[0]?.hex ?? fallbackForm.color_primary
  const accentColor = findStyleColor(sg.colors, 'accent') ?? sg.colors?.[1]?.hex ?? fallbackForm.color_accent
  const neutralColor = findStyleColor(sg.colors, 'neutral') ?? sg.colors?.[2]?.hex ?? fallbackForm.color_neutral
  const [draft, setDraft] = useState({
    summary: sg.summary ?? '',
    primary: primaryColor,
    accent: accentColor,
    neutral: neutralColor,
    colors: formatStyleColors(sg.colors),
    heading: sg.typography?.heading ?? fallbackForm.typography_heading,
    body: sg.typography?.body ?? fallbackForm.typography_body,
    typographyNotes: sg.typography?.notes ?? '',
    visualStyle: sg.visual_style ?? '',
    logoUsage: sg.logo_usage ?? '',
    gradientStyle: sg.gradient_style ?? '',
    presentationStyle: sg.presentation_style ?? '',
    imagery: sg.imagery ?? '',
    layoutRules: (sg.layout_rules ?? []).join('\n'),
    doNots: (sg.do_nots ?? []).join('\n'),
  })

  useEffect(() => {
    setDraft({
      summary: sg.summary ?? '',
      primary: primaryColor,
      accent: accentColor,
      neutral: neutralColor,
      colors: formatStyleColors(sg.colors),
      heading: sg.typography?.heading ?? fallbackForm.typography_heading,
      body: sg.typography?.body ?? fallbackForm.typography_body,
      typographyNotes: sg.typography?.notes ?? '',
      visualStyle: sg.visual_style ?? '',
      logoUsage: sg.logo_usage ?? '',
      gradientStyle: sg.gradient_style ?? '',
      presentationStyle: sg.presentation_style ?? '',
      imagery: sg.imagery ?? '',
      layoutRules: (sg.layout_rules ?? []).join('\n'),
      doNots: (sg.do_nots ?? []).join('\n'),
    })
  }, [
    profile.id,
    sg.summary,
    sg.colors,
    sg.typography?.heading,
    sg.typography?.body,
    sg.typography?.notes,
    sg.visual_style,
    sg.logo_usage,
    sg.gradient_style,
    sg.presentation_style,
    sg.imagery,
    sg.layout_rules,
    sg.do_nots,
    fallbackForm.color_primary,
    fallbackForm.color_accent,
    fallbackForm.color_neutral,
    fallbackForm.typography_heading,
    fallbackForm.typography_body,
  ])

  const saveStyleguide = async () => {
    const colors = ensureNamedColors(parseStyleColors(draft.colors), {
      primary: draft.primary,
      accent: draft.accent,
      neutral: draft.neutral,
    })
    const update: BrandStyleguide = {
      summary: draft.summary || undefined,
      colors,
      typography: {
        heading: draft.heading || undefined,
        body: draft.body || undefined,
        notes: draft.typographyNotes || undefined,
      },
      visual_style: draft.visualStyle || undefined,
      logo_usage: draft.logoUsage || undefined,
      gradient_style: draft.gradientStyle || undefined,
      presentation_style: draft.presentationStyle || undefined,
      imagery: draft.imagery || undefined,
      layout_rules: linesToList(draft.layoutRules),
      do_nots: linesToList(draft.doNots),
    }
    try {
      await updateStyleguide.mutateAsync(update)
      onFallbackChange({
        color_primary: draft.primary,
        color_accent: draft.accent,
        color_neutral: draft.neutral,
        typography_heading: draft.heading,
        typography_body: draft.body,
      })
      toast.success('Styleguide gespeichert')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Styleguide speichern fehlgeschlagen')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand Styleguide</CardTitle>
        <p className="text-sm text-muted-foreground">
          Wird verwendet in: alle Output-Skills mit Brand-Auswahl. Diese Werte sind die zentrale
          Quelle für Farben, Typografie, Layout-Regeln und visuelle Leitplanken.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-3">
          <StyleColorField
            label="Primary"
            value={draft.primary}
            onChange={(primary) => setDraft({ ...draft, primary })}
          />
          <StyleColorField
            label="Accent"
            value={draft.accent}
            onChange={(accent) => setDraft({ ...draft, accent })}
          />
          <StyleColorField
            label="Neutral"
            value={draft.neutral}
            onChange={(neutral) => setDraft({ ...draft, neutral })}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
          <FormBlock label="Heading-Font">
            <Input value={draft.heading} onChange={(e) => setDraft({ ...draft, heading: e.target.value })} />
          </FormBlock>
          <FormBlock label="Body-Font">
            <Input value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </FormBlock>
        </div>
        <FormBlock label="Zusammenfassung">
          <Textarea
            rows={3}
            value={draft.summary}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
          />
        </FormBlock>
        <FormBlock label="Farben">
          <Textarea
            rows={5}
            value={draft.colors}
            onChange={(e) => setDraft({ ...draft, colors: e.target.value })}
            placeholder="Primary | #123456 | Verwendung"
            className="font-mono text-xs"
          />
        </FormBlock>
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
          <FormBlock label="Typografie-Notizen">
            <Textarea
              rows={4}
              value={draft.typographyNotes}
              onChange={(e) => setDraft({ ...draft, typographyNotes: e.target.value })}
            />
          </FormBlock>
          <FormBlock label="Visual Style">
            <Textarea
              rows={4}
              value={draft.visualStyle}
              onChange={(e) => setDraft({ ...draft, visualStyle: e.target.value })}
            />
          </FormBlock>
        </div>
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
          <FormBlock label="Logo-Nutzung">
            <Textarea
              rows={4}
              value={draft.logoUsage}
              onChange={(e) => setDraft({ ...draft, logoUsage: e.target.value })}
            />
          </FormBlock>
          <FormBlock label="Gradient-Stil">
            <Textarea
              rows={4}
              value={draft.gradientStyle}
              onChange={(e) => setDraft({ ...draft, gradientStyle: e.target.value })}
            />
          </FormBlock>
        </div>
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
          <FormBlock label="Präsentationsstil">
            <Textarea
              rows={4}
              value={draft.presentationStyle}
              onChange={(e) => setDraft({ ...draft, presentationStyle: e.target.value })}
            />
          </FormBlock>
          <FormBlock label="Imagery">
            <Textarea
              rows={4}
              value={draft.imagery}
              onChange={(e) => setDraft({ ...draft, imagery: e.target.value })}
            />
          </FormBlock>
        </div>
        <div className="grid grid-cols-1 gap-3 min-[761px]:grid-cols-2">
          <FormBlock label="Layout-Regeln">
            <Textarea
              rows={4}
              value={draft.layoutRules}
              onChange={(e) => setDraft({ ...draft, layoutRules: e.target.value })}
              placeholder="Eine pro Zeile"
            />
          </FormBlock>
          <FormBlock label="Do-Nots">
            <Textarea
              rows={4}
              value={draft.doNots}
              onChange={(e) => setDraft({ ...draft, doNots: e.target.value })}
              placeholder="Eine pro Zeile"
            />
          </FormBlock>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => void saveStyleguide()} disabled={updateStyleguide.isPending}>
            {updateStyleguide.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Styleguide speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function FormBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

function StyleColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <FormBlock label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded border border-input bg-background"
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" />
      </div>
    </FormBlock>
  )
}

function linesToList(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

interface CreateBrandDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (id: string) => void
}

function CreateBrandDialog({ open, onOpenChange, onCreated }: CreateBrandDialogProps) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [brandType, setBrandType] = useState<BrandType>('company')
  const create = useCreateBrandProfile()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neues Brand-Profil</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormRow label="Slug">
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="z.B. flowmomentum"
              className="font-mono"
            />
          </FormRow>
          <FormRow label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anzeigename" />
          </FormRow>
          <BrandTypeControl value={brandType} onChange={setBrandType} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button
            disabled={!slug || !name || create.isPending}
            onClick={async () => {
              try {
                const created = await create.mutateAsync({ slug, name, brand_type: brandType })
                onCreated(created.id)
                toast.success(`Brand "${created.name}" angelegt`)
                setSlug('')
                setName('')
                setBrandType('company')
                onOpenChange(false)
              } catch {
                toast.error('Anlegen fehlgeschlagen')
              }
            }}
          >
            {create.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
